import { Tag, TagLabel, UnorderedList, ListItem, Wrap, WrapItem } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type FieldStatusDto,
  type SourcingEventV2Dto,
} from "../../../../../../autogen/bff-api";
import { useCpvCodes } from "../../../../../../common/cpv/useCpvCodes";
import { momentLocaleFor } from "../../../../../../common/formatting/i18nFormatters";
import { TipTap } from "../../../../../../common/input/TipTap/TipTap";
import type { ReadinessLookup, KnownFieldKey } from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import { formatContractDuration, formatYesNo } from "../../utils/displayFormatters";
import { isFrameworkEnabled } from "../../utils/publicProcurementOptions";
import { useReservedParticipationLabel } from "../../utils/useReservedParticipationLabel";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { SummaryGroupHeader } from "./SummaryGroupHeader";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "DescriptionPresent",
  "MainCpvCodeSet",
  "MainCpvCodeValid",
  "ContractDurationTypeSet",
  "OpenEndedStartDatePresent",
  "FrameworkAgreementSet",
  "FrameworkMaxParticipantsRequiredWhenSet",
  "EuFundedSet",
  "GpaCoverageSet",
  "ReservedParticipationSet",
  "ReservedExecutionSet",
  "ContractStartBeforeEnd",
  "MainCpvCodeMatchesReservedParticipation",
  "ReservedParticipationDurationWithinThreeYears",
  "MainCpvCodeMatchesContractNature",
] as const satisfies readonly KnownFieldKey[];

interface ClassificationSectionProps {
  event: SourcingEventV2Dto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const ClassificationSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
}: ClassificationSectionProps) => {
  const { t, i18n } = useTranslation();
  const { eventData, publicContext } = event;
  const { codeIndex } = useCpvCodes();
  const reservedParticipationLabel = useReservedParticipationLabel();
  const isNorwegian = i18n.language.startsWith("no");
  const momentLocale = momentLocaleFor(i18n.language);

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  const cpvCodes = publicContext.cpvCodes ?? [];
  const optionDescriptions = (eventData.options?.descriptions ?? []).filter((d) => d.trim() !== "");
  const frameworkOn = isFrameworkEnabled(publicContext.frameworkAgreement);
  const frameworkLabel = frameworkOn && publicContext.frameworkAgreement
    ? t(publicContext.frameworkAgreement)
    : t("No");
  const reservedParticipation = publicContext.reservedParticipation
    ? reservedParticipationLabel(publicContext.reservedParticipation)
    : null;
  const reservedExecution = publicContext.reservedExecution ? t(publicContext.reservedExecution) : null;
  const euFunded = formatYesNo(publicContext.euFunded, t);
  const gpaCoverage = formatYesNo(publicContext.gpaCoverage, t);
  const hasOptions = formatYesNo(eventData.options?.hasOptions ?? null, t);

  return (
    <SummarySection
      title={t("Classification and contract")}
      editTarget={{ kind: "wizard-step", step: "classification" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryGroupHeader isFirst>{t("General information")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Description")}
          value={
            eventData.description
              ? <TipTap content={eventData.description} editable={false} hasBorder={false} padding="0" />
              : <EmptyValue text={t("Not provided")} />
          }
          status={readinessFor("DescriptionPresent")}
        />
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Classification and value")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("CPV codes")}
          value={
            cpvCodes.length > 0 ? (
              <Wrap spacing="2">
                {cpvCodes.map((code) => {
                  const dto = codeIndex[code];
                  const label = dto ? (isNorwegian ? dto.nameNb : dto.nameEn) : null;
                  return (
                    <WrapItem key={code}>
                      <Tag colorScheme="teal" variant="subtle" size="md" maxW="100%">
                        <TagLabel whiteSpace="normal">
                          {code}
                          {label ? ` — ${label}` : ""}
                        </TagLabel>
                      </Tag>
                    </WrapItem>
                  );
                })}
              </Wrap>
            ) : (
              <EmptyValue text={t("Not set")} />
            )
          }
          status={readinessFor("MainCpvCodeSet")}
          extraStatuses={[
            readinessFor("MainCpvCodeValid"),
            readinessFor("MainCpvCodeMatchesReservedParticipation"),
            readinessFor("MainCpvCodeMatchesContractNature"),
          ]}
        />
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Contract period")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Contract duration")}
          value={
            eventData.contractDuration
              ? formatContractDuration(eventData.contractDuration, t, momentLocale)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("ContractDurationTypeSet")}
          extraStatuses={[
            readinessFor("OpenEndedStartDatePresent"),
            readinessFor("ContractStartBeforeEnd"),
            readinessFor("ReservedParticipationDurationWithinThreeYears"),
          ]}
        />
        <SummaryField
          label={t("Contract options")}
          value={hasOptions ?? <EmptyValue text={t("Not set")} />}
          span="half"
        />
        {eventData.options?.hasOptions === true && (
          <SummaryField
            label={t("Option descriptions")}
            value={
              optionDescriptions.length > 0 ? (
                <UnorderedList spacing="1" pl="4">
                  {optionDescriptions.map((d, i) => (
                    <ListItem key={i} fontSize="sm">{d}</ListItem>
                  ))}
                </UnorderedList>
              ) : (
                <EmptyValue text={t("No descriptions provided")} />
              )
            }
          />
        )}
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Framework agreement")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Call-off mechanism")}
          value={frameworkLabel}
          span="half"
          status={readinessFor("FrameworkAgreementSet")}
        />
        {frameworkOn && (
          <SummaryField
            label={t("Number of parties (suppliers)")}
            value={
              publicContext.frameworkMaxParticipants != null
                ? String(publicContext.frameworkMaxParticipants)
                : <EmptyValue text={t("Not set")} />
            }
            span="half"
            status={readinessFor("FrameworkMaxParticipantsRequiredWhenSet")}
          />
        )}
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Advanced settings")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("EU financing")}
          value={euFunded ?? <EmptyValue text={t("Not set")} />}
          span="half"
          status={readinessFor("EuFundedSet")}
        />
        <SummaryField
          label={t("GPA coverage")}
          value={gpaCoverage ?? <EmptyValue text={t("Not set")} />}
          span="half"
          status={readinessFor("GpaCoverageSet")}
        />
        <SummaryField
          label={t("Reserved participation")}
          value={reservedParticipation ?? <EmptyValue text={t("Not set")} />}
          span="half"
          status={readinessFor("ReservedParticipationSet")}
        />
        <SummaryField
          label={t("Reserved execution")}
          value={reservedExecution ?? <EmptyValue text={t("Not set")} />}
          span="half"
          status={readinessFor("ReservedExecutionSet")}
        />
      </SummaryFieldGroup>
    </SummarySection>
  );
};
